import { SMALL_MAX } from '@frameio/utils/breakpoints';
import type {
  MediaState,
  MediaStateIdEnum,
  ZoomSetting,
} from '../../generated/types';
import { ReactiveMap } from '../../utils/ReactiveMap';

export const mediaStatesMap = new ReactiveMap<
  string,
  Array<MediaState>,
  'mediaStatesMap'
>('mediaStatesMap');

const defaultDocumentState = {
  currentPage: 1,
  isPageDrawerAvailable:
    typeof window !== 'undefined' && window?.innerWidth > SMALL_MAX,
  isPageDrawerOpen: true,
  isPanning: false,
  isPdfLoaded: false,
  shouldFallbackToImagePane: false,
};

const defaultInteractiveState = {
  pageCount: null,
  // Full URL to the iframe content
  currentUrl: null,
  // Relative path to the html page within the manifest
  currentUrlPath: null,
  // Live URL only: origin and path of the approved page on another site the
  // iframe is showing, or null while it's on the live URL's site
  liveUrlExternalUrl: null,
  // Dimensions of the iframe
  iframeWidth: null,
  iframeHeight: null,
  // Scroll position of the iframe
  scrollX: 0,
  scrollY: 0,
  resolutionLastChangedBy: 'load',
  // Resolution type of the iframe (desktop, mobile, tablet, etc.)
  resolutionType: null,
};

const defaultViewportState = {
  activeZoom: 'FIT' as ZoomSetting,
  cachedFrameGuide: '',
  fillZoomValue: 0,
  fitZoomValue: 0,
  focalPointX: 0,
  focalPointY: 0,
  frameGuide: '',
  isDragging: false,
  isFullscreen: false,
  isMaskOn: false,
  lastZoom: 0,
  viewHeight: 0,
  viewLeft: 0,
  viewTop: 0,
  viewWidth: 0,
  zoom: 0,
};

const defaultPlayerState = {
  currentCaptionIndex: -1,
  currentHlsManifestUrl: null,
  currentTime: 0,
  currentTimeLastUpdated: 0,
  duration: 0,
  fps: 0,
  frames: 0,
  elementDuration: 0,
  hasUpdatedTime: false,
  hotkeyPlaybackRate: null,
  isReady: true,
  isSeeking: false,
  isTranscriptionPanelOpen: false,
  lastVolume: 1,
  customLoop: false,
  mediaStartUTC: null,
  muted: false,
  paused: true,
  playbackRateAudio: 1,
  playbackRateVideo: 1,
  overlayPlayButtonWasDismissed: false,
  volume: 1,
};

const defaultRangeState = {
  isRangeSelectorVisible: false,
  rangeDuration: 0,
  rangeStart: 0,
};

export const sharedMediaStateInitial: MediaState = {
  __typename: 'MediaState',
  id: '', // set below
  ...defaultPlayerState,
  ...defaultRangeState,
  ...defaultDocumentState,
  ...defaultInteractiveState,
  ...defaultViewportState,
};

const getMediaStateValue = (key: string, mediaState, mediastateIndex = 0) =>
  mediaState?.[mediastateIndex]?.[key] ?? sharedMediaStateInitial[key];

/**
 * DEFAULT MEDIA STATE (used when a media state id is not specified)
 */
export const MEDIA_STATE_DEFAULT: MediaStateIdEnum = 'MEDIA_STATE_DEFAULT';
const mediaStateDefault = mediaStatesMap.get(MEDIA_STATE_DEFAULT);
const lastVolume = getMediaStateValue('lastVolume', mediaStateDefault);
const muted = getMediaStateValue('muted', mediaStateDefault);
const playbackRateAudio = getMediaStateValue(
  'playbackRateAudio',
  mediaStateDefault
);
const playbackRateVideo = getMediaStateValue(
  'playbackRateVideo',
  mediaStateDefault
);

const overlayPlayButtonWasDismissed = getMediaStateValue(
  'overlayPlayButtonWasDismissed',
  mediaStateDefault
);

const volume = getMediaStateValue('volume', mediaStateDefault);

mediaStatesMap.clear();
mediaStatesMap.set(MEDIA_STATE_DEFAULT, [
  {
    ...sharedMediaStateInitial,
    __typename: 'MediaState',
    id: MEDIA_STATE_DEFAULT,
    lastVolume,
    muted,
    playbackRateAudio,
    playbackRateVideo,
    overlayPlayButtonWasDismissed,
    volume,
  },
]);

/**
 * QUICKLOOK MEDIA STATE (used in the quicklook viewer)
 * - should default to auto-play
 * - should default to muted: false, volume: 1
 */
export const MEDIA_STATE_QUICKLOOK: MediaStateIdEnum = 'MEDIA_STATE_QUICKLOOK';
const quicklookMediaState = mediaStatesMap.get(MEDIA_STATE_QUICKLOOK);

const quicklookMediaStateLastVolume = getMediaStateValue(
  'lastVolume',
  quicklookMediaState
);
const quicklookMediaStatevolume = getMediaStateValue(
  'volume',
  quicklookMediaState
);
const quicklookMediaStateMuted = getMediaStateValue(
  'muted',
  quicklookMediaState
);

mediaStatesMap.set(MEDIA_STATE_QUICKLOOK, [
  {
    ...sharedMediaStateInitial,
    __typename: 'MediaState',
    id: MEDIA_STATE_QUICKLOOK,
    lastVolume: quicklookMediaStateLastVolume,
    muted: quicklookMediaStateMuted,
    paused: false,
    volume: quicklookMediaStatevolume,
  },
]);
